<template>
  <div class="quick-search-wrapper" :class="{ 'is-open': isOpen, 'is-always-open': alwaysOpen }">
    <!-- En mode `alwaysOpen` le champ est rendu en permanence : l'icône bascule
         devient inutile et la recherche occupe directement la zone centrale. -->
    <div v-if="!alwaysOpen" class="search-icon-btn" :class="{ 'icon-active': isOpen }" @click="toggleSearch">
      <div class="icon-circle" :class="{ 'icon-small': isOpen }" style="">
        <q-icon
          :name="isOpen ? 'close' : 'search'"
          size="md"
          :color="isOpen ? 'white' : 'grey-8'"
          class="icon-flip"
          :style="isOpen ? 'font-size: 20px;':'font-size: 32px;'"
        />
      </div>
      <q-tooltip>{{ isOpen ? 'Fermer' : $t('main.quickSearch.tooltip') }}</q-tooltip>
    </div>

    <transition name="fade-scale">
      <q-input
        v-if="isOpen"
        ref="searchInput"
        v-model="searchQuery"
        dense
        outlined
        :placeholder="$t('main.quickSearch.placeholder')"
        class="search-input-center"
        @update:model-value="onSearchInput"
        @input="onSearchInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown.enter.prevent="handleEnter"
        @keydown.down.prevent="navigateResults('down')"
        @keydown.up.prevent="navigateResults('up')"
        @keydown.escape="closeSearch"
        bg-color="white"  
        style="margin-bottom: 5px;"
      >
        <template v-slot:prepend>
          <q-icon :name="detectedTypeIcon" size="sm" :color="searchQuery ? 'primary' : 'grey-6'">
            <q-tooltip v-if="searchQuery">{{ detectedTypeLabel }}</q-tooltip>
          </q-icon>
        </template>

        <template v-slot:append>
          <q-icon
            v-if="searchQuery"
            name="close"
            class="cursor-pointer"
            size="xs"
            @click="clearSearch"
          />
          <q-spinner v-if="isLoading" size="18px" color="primary" />
        </template>
      </q-input>
    </transition>

    <div v-if="isOpen && (isFocused || showResults)" class="quick-search-dropdown">
      <div class="activity-chips-wrapper" @mousedown.prevent>
        <span class="activity-label">{{ $t('main.dossier.filtreMenu.activity') }}:</span>
        <q-chip
          v-for="activity in activityOptions"
          :key="activity.value"
          clickable
          :color="selectedActivity === activity.value ? (activity.value ? 'blue' : 'positive') : 'grey-3'"
          :text-color="selectedActivity === activity.value ? 'white' : 'grey-8'"
          @click="selectActivity(activity.value)"
          class="activity-chip"
          icon="none"
        >
          {{ activity.label }}
        </q-chip>
      </div>

      <div v-if="isLoading && searchQuery" class="skeleton-loading" @mousedown.prevent>
        <div class="skeleton-item" v-for="n in 3" :key="n">
          <q-skeleton type="circle" size="28px" />
          <div class="skeleton-content">
            <q-skeleton type="text" width="40%" />
            <q-skeleton type="text" width="70%" class="q-mt-xs" />
          </div>
        </div>
      </div>

      <div v-if="showResults && searchResults.length > 0 && !isLoading" class="search-results-dropdown" @mousedown.prevent>
        <div class="results-header">
          <span class="results-count">{{ searchResults.length }} {{ $t('main.quickSearch.results') }}</span>
        </div>
        <q-list class="results-list">
          <q-item
            v-for="(result, index) in searchResults"
            :key="result.resourceUid"
            clickable
            v-ripple
            :class="{ 'active-result': index === selectedResultIndex }"
            @click="selectResult(result)"
            @mouseenter="selectedResultIndex = index"
          >
            <q-item-section avatar>
              <q-avatar :color="getStatusColor(result.statusCode)" text-color="white" size="28px">
                <q-icon name="receipt_long" size="xs" />
              </q-avatar>
            </q-item-section>

            <q-item-section>
              <q-item-label class="result-title-row">
                <strong class="result-number">{{ result.externalReference }}</strong>
              </q-item-label>

              <q-item-label caption class="result-info-row q-mt-xs">
                <div class="info-item">
                  <q-icon name="precision_manufacturing" size="14px" color="blue-grey-7" />
                  <span class="info-text">{{ result.manufacturingNumber || '-' }}</span>
                </div>
                <div class="info-item">
                  <q-icon name="directions_car" size="14px" color="blue-grey-7" />
                  <span class="info-text">{{ result.chassisReference || '-' }}</span>
                </div>
              </q-item-label>

              <q-item-label caption class="result-info-row q-mt-xs">
                <div class="info-item">
                  <q-icon name="pin_drop" size="14px" color="blue-grey-7" />
                  <span class="info-text">{{ result.registrationNumber || '-' }}</span>
                </div>
                <div class="info-item">
                  <q-icon name="person_outline" size="14px" color="blue-grey-7" />
                  <span class="info-text">{{ result.clientName || '-' }}</span>
                </div>
              </q-item-label>
            </q-item-section>

            <q-item-section side>
              <q-icon name="arrow_forward" color="primary" />
            </q-item-section>
          </q-item>
        </q-list>
      </div>

      <div v-if="showResults && searchQuery && searchResults.length === 0 && !isLoading" class="no-results-modern" @mousedown.prevent>
        <q-icon name="search_off" size="48px" color="grey-4" />
        <p class="no-results-title">{{ $t('main.quickSearch.noResults') }}</p>
        <p class="no-results-hint">Essayez N° Commande, Chassis, Immatriculation...</p>
      </div>

      <div v-if="isFocused && !searchQuery && !isLoading" class="hints-footer" @mousedown.prevent>
        <div class="status-legend">
          <span class="legend-title">STATUTS:</span>
          <div class="legend-item">
            <div class="legend-dot bg-positive"></div>
            <span>Validé</span>
          </div>
          <div class="legend-item">
            <div class="legend-dot bg-blue"></div>
            <span>Confirmé</span>
          </div>
          <div class="legend-item">
            <div class="legend-dot bg-orange"></div>
            <span>En cours</span>
          </div>
          <div class="legend-item">
            <div class="legend-dot bg-grey"></div>
            <span>Brouillon</span>
          </div>
        </div>

        <div class="keyboard-hints">
          <div class="hint-item">
            <kbd>↑↓</kbd> Naviguer
          </div>
          <div class="hint-item">
            <kbd>Enter</kbd> Ouvrir
          </div>
          <div class="hint-item">
            <kbd>Esc</kbd> Fermer
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import { quickSearchOrder } from "@/store/services/order/orderApi";
import router from "@/router";
import i18n from "@/i18n";
import store from "@/store";
import { formatDateLocale } from "@/utils/configuration/formatters-config";
import { setFontAndSize } from "pdf-lib";

interface SearchResult {
  resourceUid: string;
  externalReference?: string;
  manufacturingNumber?: string;
  registrationNumber?: string;
  chassisReference?: string;
  clientName?: string;
  activity?: string;
  statusCode?: string;
  createdDate?: string;
}

type SearchType = 'externalReference' | 'manufacturingNumber' | 'registrationNumber' | 'chassisReference' | 'clientReference' | 'auto';

@Options({
  name: "QuickSearch",
  props: {
    /** Champ affiché en permanence (barre centrale du header) au lieu du mode icône. */
    alwaysOpen: {
      type: Boolean,
      default: false,
    },
  },
})
export default class QuickSearch extends Vue {
  alwaysOpen!: boolean;
  isOpen = false;
  isFocused = false;
  searchQuery = "";
  selectedActivity: boolean | null = null;
  searchResults: SearchResult[] = [];
  isLoading = false;
  showResults = false;
  selectedResultIndex = -1;
  searchTimeout: any = null;
  blurTimeout: any = null;
  activityOptions: any[] = [];
  recentSearches: string[] = [];

  mounted() {
    if (this.alwaysOpen) {
      this.isOpen = true;
    }
    this.loadActivityOptions();
    this.loadRecentSearches();
    document.addEventListener('click', this.handleClickOutside);
  }

  loadRecentSearches() {
    const saved = localStorage.getItem('quicksearch_recent');
    if (saved) {
      this.recentSearches = JSON.parse(saved);
    }
  }

  saveRecentSearch(query: string) {
    if (!query || query.length < 2) return;

    // Remove if already exists
    this.recentSearches = this.recentSearches.filter(s => s !== query);

    // Add to beginning
    this.recentSearches.unshift(query);

    // Keep only last 5
    this.recentSearches = this.recentSearches.slice(0, 5);

    // Save to localStorage
    localStorage.setItem('quicksearch_recent', JSON.stringify(this.recentSearches));

    console.log('Recent searches saved:', this.recentSearches);
  }

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout);
    }
  }

  handleClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!this.isOpen || target.closest('.quick-search-wrapper')) {
      return;
    }
    if (this.alwaysOpen) {
      // Champ permanent : on referme seulement le panneau de résultats et on
      // conserve la saisie, que l'utilisateur n'a pas demandé à perdre.
      this.isFocused = false;
      this.showResults = false;
      return;
    }
    this.closeSearch();
  }

  loadActivityOptions() {
    this.activityOptions = [
      { label: 'VN', value: true },
      { label: 'VO', value: false },
    ];
  }

  toggleSearch() {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.$nextTick(() => {
        (this.$refs.searchInput as any)?.$el?.querySelector('input')?.focus();
      });
    } else {
      this.clearSearch();
    }
  }

  openSearch() {
    this.isOpen = true;
    this.$nextTick(() => {
      (this.$refs.searchInput as any)?.$el?.querySelector('input')?.focus();
    });
  }

  onFocus() {
    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout);
    }
    this.isFocused = true;
  }

  onBlur() {
    this.blurTimeout = setTimeout(() => {
      if (!this.isOpen) return;
      this.isFocused = false;
      if (!this.searchQuery) {
        this.showResults = false;
      }
    }, 300);
  }

  closeSearch() {
    // En mode `alwaysOpen` le champ ne se replie jamais : on se contente de le vider.
    if (!this.alwaysOpen) {
      this.isOpen = false;
    }
    this.isFocused = false;
    this.clearSearch();
  }

  clearSearch() {
    this.searchQuery = "";
    this.searchResults = [];
    this.showResults = false;
    this.selectedResultIndex = -1;
  }

  selectActivity(activityValue: boolean) {
    this.selectedActivity = this.selectedActivity === activityValue ? null : activityValue;
    if (this.searchQuery && this.searchQuery.trim().length >= 2) {
      this.performSearch();
    }
  }

  clearActivity() {
    this.selectedActivity = null;
    if (this.searchQuery && this.searchQuery.trim().length >= 2) {
      this.performSearch();
    }
  }

  onSearchInput() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    if (!this.searchQuery || this.searchQuery.trim().length === 0) {
      this.searchResults = [];
      this.showResults = false;
      return;
    }

    this.searchTimeout = setTimeout(() => {
      if (this.searchQuery.trim().length >= 2) {
        this.performSearch();
      }
    }, 400);
  }

  async performSearch() {
    if (!this.searchQuery.trim()) return;

    this.isLoading = true;
    this.showResults = true;

    try {
      const searchValue = this.searchQuery.trim();
      const filter: any = {
        searchValue,
        userNetwork: store.getters["authModule/getSelectedUserNetwork"],
      };

      if (this.selectedActivity !== null) {
        filter.activity = this.selectedActivity;
      }

      const results = await quickSearchOrder(filter, 0, 10);

      this.searchResults = results.map((order: any) => ({
        resourceUid: order.orderid,
        externalReference: order.externalreference,
        manufacturingNumber: order.num_fabrication,
        registrationNumber: order.matriculation,
        chassisReference: order.chassis_reference,
        clientName: order.associatedpartyname || `${order.associatedpartyfirstname || ''} ${order.associatedpartyfamilyname || ''}`.trim(),
        activity: order.activity ? 'VN' : 'VO',
        statusCode: order.status_code,
        createdDate: order.ordercreateddate,
      }));

      this.selectedResultIndex = this.searchResults.length > 0 ? 0 : -1;

      if (this.searchResults.length > 0) {
        this.saveRecentSearch(this.searchQuery.trim());
      }
    } catch (error) {
      console.error("Search error:", error);
      this.searchResults = [];
    } finally {
      this.isLoading = false;
    }
  }

  detectSearchType(query: string): SearchType {
    const trimmedQuery = query.trim();

    if (/^[A-HJ-NPR-Z0-9]{17}$/i.test(trimmedQuery)) {
      return 'chassisReference';
    }

    if (/^[A-Z]{2}-?\d{3}-?[A-Z]{2}$/i.test(trimmedQuery) || /^\d{1,4}\s?[A-Z]{2,3}\s?\d{2,3}$/i.test(trimmedQuery)) {
      return 'registrationNumber';
    }

    if (/^ORD\d{6}$/i.test(trimmedQuery)) {
      return 'clientReference';
    }

    if (/^C\d{4}$/i.test(trimmedQuery)) {
      return 'manufacturingNumber';
    }

    return 'externalReference';
  }

  buildSearchFilter(searchType: SearchType): any {
    const filter: any = {
      userNetwork: store.getters["authModule/getSelectedUserNetwork"],
      documentStatus: "",
      filterReference: "",
      externalReference: "",
      manufacturingNumber: "",
      registrationNumber: "",
      delivered: "",
      notDelivered: "",
      chassisReference: "",
      deliveryDateFrom: "",
      deliveryDateTo: "",
      deliveryDateToggle: null,
      supplierName: "",
      clientReference: "",
      firstName: "",
      family_name: "",
      commercial_name: "",
      name: "",
      date_RDVToggle: false,
      date_MADAToggle: false,
      date_MADCToggle: false,
      chassis_NumberToggle: null,
    };

    if (this.selectedActivity !== null) {
      filter.activity = this.selectedActivity;
    }

    const searchValue = this.searchQuery.trim();

    switch (searchType) {
      case 'chassisReference':
        filter.chassisReference = searchValue;
        break;
      case 'registrationNumber':
        filter.registrationNumber = searchValue;
        break;
      case 'manufacturingNumber':
        filter.manufacturingNumber = searchValue;
        break;
      case 'clientReference':
        filter.filterReference = searchValue;
        break;
      case 'externalReference':
      default:
        filter.externalReference = searchValue;
        break;
    }

    return filter;
  }

  get detectedTypeIcon(): string {
    if (!this.searchQuery) return 'search';

    const type = this.detectSearchType(this.searchQuery);
    const icons: Record<SearchType, string> = {
      chassisReference: 'directions_car',
      registrationNumber: 'pin',
      manufacturingNumber: 'precision_manufacturing',
      clientReference: 'folder',
      externalReference: 'receipt',
      auto: 'search',
    };

    return icons[type] || 'search';
  }

  get detectedTypeLabel(): string {
    if (!this.searchQuery) return this.$t('main.quickSearch.enterQuery');

    const type = this.detectSearchType(this.searchQuery);
    return this.$t(`main.dossier.filtreMenu.${type}`);
  }

  handleEnter() {
    if (this.selectedResultIndex >= 0 && this.searchResults[this.selectedResultIndex]) {
      this.selectResult(this.searchResults[this.selectedResultIndex]);
    }
    else if (this.searchResults.length === 1) {
      this.selectResult(this.searchResults[0]);
    }
    else if (this.searchQuery.trim() && !this.showResults) {
      this.performSearch();
    }
  }

  navigateResults(direction: 'up' | 'down') {
    if (!this.showResults || this.searchResults.length === 0) return;

    if (direction === 'down') {
      this.selectedResultIndex = Math.min(this.selectedResultIndex + 1, this.searchResults.length - 1);
    } else {
      this.selectedResultIndex = Math.max(this.selectedResultIndex - 1, 0);
    }

    this.$nextTick(() => {
      const selectedElement = document.querySelector('.active-result');
      selectedElement?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  }

  async selectResult(result: SearchResult) {
    const uid = result.resourceUid;
    if (!uid) return;

    this.closeSearch();

    const targetPath = `/order/edit/${uid}`;
    const currentPath = router.currentRoute.value.path;

    if (currentPath === targetPath) {
      // Reload if already on the exact page
      window.location.reload();
      return;
    }

    if (currentPath.startsWith('/order/edit/')) {
      // Navigate by replacing the location to avoid double history entry
      window.location.href = targetPath;
      return;
    }

    // Normal route navigation
    await router.push(targetPath);
    await this.$nextTick();
  }


  formatDate(date: string): string {
    if (!date) return '';
    return formatDateLocale(date, 'YYYY-MM-DD', i18n.global.locale.value, i18n.global.t("mask.format"));
  }

  getStatusColor(statusCode?: string): string {
    const colors: Record<string, string> = {
      'ORDER_FG_VALIDATED': 'positive',
      'DRAFT': 'grey',
      'CONFIRMED': 'blue',
      'IN_PROGRESS': 'orange',
      'COMPLETED': 'green',
      'CANCELLED': 'red',
      'DELIVERED': 'teal',
    };
    return colors[statusCode || ''] || 'grey';
  }

  getActivityColor(activity?: string): string {
    const colors: Record<string, string> = {
      'VN': 'blue',
      'VO': 'positive',
    };
    return colors[activity || ''] || 'grey';
  }
}
</script>

<style scoped lang="scss">
.quick-search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 26px;
}

.search-icon-btn {
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.3s ease;

  &.icon-active {
    order: 2;
  }
}

.icon-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #f8f9fa;
  border: 2px solid #e8eef3;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: #e9ecef;
    border-color: #cbd5e0;
  }

  &.icon-small {
    width: 24px;
    height: 24px;
  }

  .search-icon-btn.icon-active & {
    background: #027be3;
    border-color: #027be3;

    &:hover {
      background: #0266c0;
      border-color: #0266c0;
    }
  }
}

.icon-flip {
  transition: transform 0.3s ease;
  transform: scaleX(-1);

  .search-icon-btn.icon-active & {
    transform: scaleX(1);
  }
}

.search-input-center {
  width: 450px;
  order: 1;
  position: relative;
}

.quick-search-dropdown {
  position: absolute;
  top: calc(100% - 2px);
  left: 0;
  width: 450px;
  z-index: 9999;
  background: white;
  border-radius: 0 0 6px 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

.fade-scale-enter-active {
  transition: all 0.3s ease;
}

.fade-scale-leave-active {
  transition: all 0.3s ease;
}

.fade-scale-enter-from {
  opacity: 0;
  transform: translateX(-50%) scale(0.95);
}

.fade-scale-leave-to {
  opacity: 0;
  transform: translateX(-50%) scale(0.95);
}

.activity-chips-wrapper {
  display: flex;
  gap: 4px;
  padding: 8px 12px;
  background: #fafbfc;
  align-items: center;
  flex-wrap: wrap;
  border-bottom: 1px solid #e8eef3;
}

.activity-label {
  font-size: 11px;
  font-weight: 700;
  color: #6c757d;
  text-transform: uppercase;
  margin-right: 4px;
}

.activity-chip {
  font-size: 11px;
  height: 24px;
  font-weight: 600;

  ::v-deep .q-chip__icon {
    display: none !important;
  }
}

.search-results-dropdown {
  max-height: 400px;
  overflow-y: auto;
  background: white;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}

.results-header {
  padding: 8px 12px;
  background: #f8f9fa;
  border-bottom: 1px solid #e8eef3;
}

.results-count {
  font-size: 10px;
  font-weight: 700;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.results-list {
  padding: 0;
  background: white;

  .q-item {
    padding: 10px 12px;
    background: white;

    &:hover, &.active-result {
      background: #f8f9fa;
      border-left: 2px solid #027be3;
    }

    &:not(:last-child) {
      border-bottom: 1px solid #e8eef3;
    }
  }
}

.result-title-row {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}

.result-number {
  font-size: 11px;
  font-weight: 600;
  color: #212529;
}

.result-info-row {
  display: flex;
  gap: 16px;
  align-items: center;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  flex: 1;
}

.info-text {
  font-size: 11px;
  color: #6c757d;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.skeleton-loading {
  padding: 12px;
  background: white;
}

.skeleton-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  margin-bottom: 8px;
  border-radius: 8px;
  background: #f8f9fa;
}

.skeleton-content {
  flex: 1;
}

.no-results-modern {
  padding: 48px 20px;
  text-align: center;
  background: white;
}

.no-results-title {
  font-size: 15px;
  font-weight: 600;
  color: #495057;
  margin: 16px 0 8px 0;
}

.no-results-hint {
  font-size: 12px;
  color: #6c757d;
  margin: 0;
}

.hints-footer {
  background: linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%);
  border-top: 1px solid #e8eef3;
}

.status-legend {
  padding: 12px 16px;
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  border-bottom: 1px solid #e8eef3;
}

.legend-title {
  font-size: 9px;
  font-weight: 800;
  color: #6c757d;
  letter-spacing: 0.5px;
  margin-right: 4px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: #495057;
  font-weight: 600;
}

.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;

  &.bg-positive {
    background: #21ba45;
  }

  &.bg-blue {
    background: #2185d0;
  }

  &.bg-orange {
    background: #f2711c;
  }

  &.bg-grey {
    background: #767676;
  }
}

.keyboard-hints {
  padding: 10px 16px;
  background: transparent;
  display: flex;
  gap: 12px;
  justify-content: center;
}

.hint-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  color: #6c757d;
  font-weight: 600;

  kbd {
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 3px;
    padding: 2px 5px;
    font-size: 9px;
    font-weight: 700;
    color: #495057;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
}

.search-results-dropdown::-webkit-scrollbar {
  width: 4px;
}

.search-results-dropdown::-webkit-scrollbar-track {
  background: transparent;
}

.search-results-dropdown::-webkit-scrollbar-thumb {
  background: #dee2e6;
  border-radius: 2px;

  &:hover {
    background: #cbd5e0;
  }
}
</style>
